import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Card, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import ResponsiveTooltip from '../../ResponsiveTooltip';

interface ProfileSectionProps {
    supportId?: string;
    currentCtUser?: any;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({
    supportId,
    currentCtUser,
}) => {
    const { t } = useTranslation();

    return (
        <Card sx={{ p: 3 }}>
            <Typography
                variant="h6"
                sx={{ mb: 3 }}
            >
                {t('settings.profile.title')}
            </Typography>

            <Stack spacing={3}>

                {/* User ID section with info icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        fontWeight="bold"
                        sx={{ minWidth: '160px' }}
                    >
                        User-ID:
                    </Typography>
                    <Typography variant="body1">
                        {supportId || '-'}
                    </Typography>
                    <ResponsiveTooltip
                        title={
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {t('settings.support.description')}
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="right"
                        slotProps={{
                            tooltip: {
                                sx: {
                                    p: 0,
                                    bgcolor: (theme) => theme.palette.background.paper,
                                    color: (theme) => theme.palette.text.primary,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                    border: '1px solid',
                                    borderColor: (theme) => theme.palette.divider,
                                    borderRadius: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    '& .MuiTooltip-arrow': {
                                        color: (theme) => theme.palette.background.paper,
                                    },
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        p: 0,
                                    },
                                },
                            },
                        }}
                    >
                        <InfoOutlinedIcon
                            sx={{
                                ml: 0.5,
                                fontSize: '1.1rem',
                                color: 'text.secondary',
                                cursor: 'help',
                            }}
                        />
                    </ResponsiveTooltip>
                </Box>

                {/* Country */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="bold" sx={{ minWidth: '160px' }}>
                        {t('settings.tax.country')}:
                    </Typography>
                    <Typography variant="body1">
                        {t('settings.tax.values.germany')}
                    </Typography>
                </Box>

                {/* Currency */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="bold" sx={{ minWidth: '160px' }}>
                        {t('settings.tax.localCurrency')}:
                    </Typography>
                    <Typography variant="body1">
                        {currentCtUser?.currency || 'EUR'}
                    </Typography>
                </Box>

                {/* Inventory Method */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="bold" sx={{ minWidth: '160px' }}>
                        {t('settings.tax.inventoryMethod')}:
                    </Typography>
                    <Typography variant="body1">
                        {t('settings.tax.values.fifo')}
                    </Typography>
                    <ResponsiveTooltip 
                        title={
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {t('settings.tax.tooltips.inventoryMethod')}
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="right"
                        slotProps={{
                            tooltip: {
                                sx: {
                                    p: 0,
                                    bgcolor: (theme) => theme.palette.background.paper,
                                    color: (theme) => theme.palette.text.primary,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                    border: '1px solid',
                                    borderColor: (theme) => theme.palette.divider,
                                    borderRadius: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    '& .MuiTooltip-arrow': {
                                        color: (theme) => theme.palette.background.paper,
                                    },
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        p: 0,
                                    },
                                },
                            },
                        }}
                    >
                        <InfoOutlinedIcon 
                            sx={{
                                ml: 0.5,
                                fontSize: '1.1rem',
                                color: 'text.secondary',
                                cursor: 'help',
                            }} 
                        />
                    </ResponsiveTooltip>
                </Box>

                {/* Cost Basis */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="bold" sx={{ minWidth: '160px' }}>
                        {t('settings.tax.costBasis')}:
                    </Typography>
                    <Typography variant="body1">
                        {t('settings.tax.values.perAccount')}
                    </Typography>
                    <ResponsiveTooltip 
                        title={
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {t('settings.tax.tooltips.costBasis')}
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="right"
                        slotProps={{
                            tooltip: {
                                sx: {
                                    p: 0,
                                    bgcolor: (theme) => theme.palette.background.paper,
                                    color: (theme) => theme.palette.text.primary,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                    border: '1px solid',
                                    borderColor: (theme) => theme.palette.divider,
                                    borderRadius: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    '& .MuiTooltip-arrow': {
                                        color: (theme) => theme.palette.background.paper,
                                    },
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        p: 0,
                                    },
                                },
                            },
                        }}
                    >
                        <InfoOutlinedIcon 
                            sx={{
                                ml: 0.5,
                                fontSize: '1.1rem',
                                color: 'text.secondary',
                                cursor: 'help',
                            }} 
                        />
                    </ResponsiveTooltip>
                </Box>

            </Stack>
        </Card>
    );
};

export default ProfileSection;
